import React, { useEffect, useState } from 'react'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/Card'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { useDispatch, useSelector } from 'react-redux'
import { login, verifyMfaLogin, ssoInitiate } from '../../store/actions/auth/login'
import Logo from "../../assets/images/logo-dark.svg"
import { Tokens } from '../../constants'
import { getSecureItem } from '../../lib/secureStorage'
import { Eye, EyeOff } from 'lucide-react'
import ForgetPasswordModal from './ForgetPasswordModal'
import Loading from '@/shared/common/Loading'

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(state => state.isLoading ?? null);
  const [forgetModal, setForgetModal] = useState(false);
  const [error, setError] = useState({});
  const token = getSecureItem(Tokens.ADMIN_TOKEN);
  const [userInfo, setUserInfo] = useState({
    email: '',
    password: '',
  })
  const [passwordVisible, setPasswordVisible] = useState(false); // Add this state
  // ORG-007: when the account has a second factor the token stays server-
  // side until the code verifies; this holds the pending challenge.
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  // ORG-008: SSO mode asks only for the email; the domain picks the IdP.
  const [ssoMode, setSsoMode] = useState(false);


  const handleChange = (e) => {
    setUserInfo({ ...userInfo, [e.target.name]: e.target.value })
  }

  const handleValidation = () => {
    let errors = {};
    let isValid = true;
    if (userInfo.email === '') {
      errors['email'] = 'Please enter your email';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email.trim())) {
      errors['email'] = 'Please enter a valid email address';
      isValid = false;
    }
    if (userInfo.password === '') {
      errors['password'] = 'Please enter a password';
      isValid = false;
    }

    setError(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    if (ssoMode) {
      const errors = {};
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email.trim())) {
        errors['email'] = 'Please enter your work email address';
      }
      setError(errors);
      if (!Object.keys(errors).length) dispatch(ssoInitiate(userInfo.email.trim()));
      return;
    }
    if (handleValidation()) {
      dispatch(login(userInfo, { onMfaChallenge: (challengeId) => setMfaChallenge(challengeId) }))
    }
  }
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handleMfaSubmit = () => {
    if (!mfaCode.trim()) return;
    dispatch(verifyMfaLogin({ challengeId: mfaChallenge, code: mfaCode.trim() }))
  }

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <div className='flex justify-center items-center  h-screen p-4'>
      <Card className="max-w-2xl w-full relative shadow-none rounded-sm" >
        <div className='flex justify-center items-center pt-4'>
          <img className='absolute -top-12 left-[30%] w-56' src={Logo} alt="logo image" />
        </div>
        <CardHeader>
          <CardTitle className="text-2xl font-500">{mfaChallenge ? 'Two-factor code' : 'Sign in'}</CardTitle>
          <CardDescription>
          {mfaChallenge ? 'Enter the 6-digit code from your authenticator app, or a recovery code.' : 'Welcome back — sign in to continue.'}
          </CardDescription>
        </CardHeader>
        {mfaChallenge ? (
          <>
            <CardContent className="space-y-2">
              <div className="space-y-1">
                <Label htmlFor="mfaCode">Authentication code</Label>
                <Input
                  id="mfaCode"
                  autoFocus
                  value={mfaCode}
                  placeholder="123456"
                  onChange={(e) => setMfaCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleMfaSubmit() }}
                  autoComplete='one-time-code'
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col items-center gap-2">
              <Button className="w-full" onClick={handleMfaSubmit} disabled={isLoading || !mfaCode.trim()}>
                {isLoading ? <> <Loading width={16} height={16} color="#fff" /> Verifying...</> : 'Verify'}
              </Button>
              <button type="button" className="text-sm text-gray-600 hover:underline" onClick={() => { setMfaChallenge(null); setMfaCode('') }}>
                Back to sign in
              </button>
            </CardFooter>
          </>
        ) : (
        <>
        <CardContent className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" name="email" placeholder="name@gmail.com" onChange={(e) => handleChange(e)} onKeyDown={handleKeyPress} autoComplete='off' />
            <div className={error['email'] ? "text-rose-600 text-xs" : ''}>{error['email'] ? error['email'] : null}</div>
          </div>
          {!ssoMode && (
          <div className="space-y-1 relative">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type={passwordVisible ? 'text' : 'password'} name="password" placeholder="********" onChange={handleChange} onKeyDown={handleKeyPress} autoComplete='off' />
            <Button type="button" aria-label={passwordVisible ? 'Hide password' : 'Show password'} className="w-12 bg-transparent order-last py-2 px-0  hover:!bg-transparent border-s absolute right-0 top-[1.6rem] rounded-ss-none rounded-es-none" onClick={togglePasswordVisibility}>
              {passwordVisible ? <EyeOff className='text-black' /> : <Eye className='text-black' />}
            </Button>
            <div className={error['password'] ? "text-rose-600 text-xs" : ''}>{error['password'] ? error['password'] : null}</div>
          </div>
          )}
          {ssoMode && (
            <p className="text-xs text-gray-500">
              You'll be sent to your organisation's identity provider to sign in.
            </p>
          )}
        </CardContent>
        <CardFooter className="sm:flex flex-col items-center justify-between block space-y-2">
          <Button className="w-full" onClick={handleSubmit} disabled={isLoading}> {isLoading ? <> <Loading width={16} height={16} color="#fff" /> Continuing...</> : (ssoMode ? 'Continue with SSO' : 'Continue')}</Button>
          <button
            type="button"
            onClick={() => { setSsoMode(!ssoMode); setError({}) }}
            className="text-sm font-semibold hover:underline text-gray-700 cursor-pointer w-full text-center"
          >
            {ssoMode ? 'Sign in with a password instead' : 'Sign in with single sign-on (SSO)'}
          </button>
          <div className="flex justify-between items-center w-full">
            <Link to="/signup" className="text-[13px]">
              Don't have an account?{" "}
              <span className="text-sm font-semibold hover:underline text-gray-700">
                Sign Up
              </span>
            </Link>
            <button
              type="button"
              onClick={() => setForgetModal(true)}
              className="text-sm font-semibold hover:underline text-gray-700 cursor-pointer"
            >
              Forgot Password?
            </button>
          </div>
        </CardFooter>
        </>
        )}
        <ForgetPasswordModal show={forgetModal} onHide={() => setForgetModal(false)} />
      </Card>
    </div>
  )
}

export default Login
